import { Link, Outlet, useNavigate } from '@tanstack/react-router';
import { m } from 'motion/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminEuQuery, sairAdmin, usarMinhaConta, type Admin } from '../api/admin';
import { inicioAdmin, podeAdmin, type ModuloAdmin } from '../../shared/permissoes-admin';
import { MOLA_TROCA } from '../lib/movimento';
import { AnunciadorDeRota } from './AnunciadorDeRota';
import { Icone, type NomeIcone } from './Icone';
import { Logo } from './Logo';
import { PularParaConteudo } from './PularParaConteudo';

/** `modulo`: aparece só para quem pode ver o módulo. `soDono`: Usuários. Sem nenhum dos dois: todo admin. */
type LinkAdmin = { to: string; texto: string; icone: NomeIcone; modulo?: ModuloAdmin; soDono?: boolean };
const LINKS: LinkAdmin[] = [
  { to: '/admin/metricas', texto: 'Números', icone: 'painel', modulo: 'metricas' },
  { to: '/admin/sugestoes', texto: 'Sugestões', icone: 'sugestao', modulo: 'sugestoes' },
  { to: '/admin/verificacoes', texto: 'Cadastros', icone: 'verificado', modulo: 'cadastros' },
  { to: '/admin/alteracoes', texto: 'Alterações', icone: 'pedidos', modulo: 'alteracoes' },
  { to: '/admin/disputas', texto: 'Contestações', icone: 'protegido', modulo: 'disputas' },
  { to: '/admin/prazos', texto: 'Prazos', icone: 'agenda', modulo: 'prazos' },
  { to: '/admin/garantias', texto: 'Garantias', icone: 'protegido', modulo: 'garantias' },
  { to: '/admin/denuncias', texto: 'Denúncias', icone: 'avisos', modulo: 'denuncias' },
  { to: '/admin/suporte', texto: 'Suporte', icone: 'conversas', modulo: 'suporte' },
  { to: '/admin/configuracao', texto: 'Integrações', icone: 'ajustes', modulo: 'integracoes' },
  { to: '/admin/monetizacao', texto: 'Monetização', icone: 'dinheiro', modulo: 'monetizacao' },
  { to: '/admin/pessoas', texto: 'Pessoas', icone: 'pessoas', modulo: 'pessoas' },
  { to: '/admin/profissionais', texto: 'Parceiros', icone: 'trabalho', modulo: 'parceiros' },
  { to: '/admin/acesso', texto: 'Acessar como', icone: 'buscar', soDono: true },
  { to: '/admin/usuarios', texto: 'Usuários', icone: 'conta', soDono: true },
  { to: '/admin/conta', texto: 'Minha conta', icone: 'conta' },
];

function visivel(l: LinkAdmin, admin: Admin): boolean {
  if (l.soDono) return admin.papel === 'dono';
  return l.modulo ? podeAdmin(admin, l.modulo) : true;
}

/**
 * Painel da equipe. Mesma Maré do app, com o topo no azul profundo
 * dizendo "área interna". Abaixo de 64rem a navegação vira uma fileira com
 * rolagem própria, para nenhuma fila ficar inalcançável no tablet.
 */
export function LayoutAdmin() {
  const { data: admin } = useQuery(adminEuQuery);
  const navegar = useNavigate();
  const qc = useQueryClient();
  const usarApp = useMutation({
    mutationFn: (_destino: string) => usarMinhaConta(),
    onSuccess: (_r, destino) => window.location.assign(destino),
  });
  const sair = useMutation({
    mutationFn: sairAdmin,
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ['admin'] }); await navegar({ to: '/admin/entrar' }); },
  });

  return (
    <div className="admin-shell">
      <PularParaConteudo />
      <AnunciadorDeRota />
      <header className="cabecalho-admin">
        <div className="cabecalho-admin-conteudo">
          <Link to={(admin ? inicioAdmin(admin) : '/admin/entrar') as never} className="marca-site" aria-label="Painel da equipe">
            <Logo tamanho={25} /><span className="cabecalho-admin-nome">Painel da equipe</span>
          </Link>
          {admin && (
            <div className="cabecalho-admin-acoes">
              <span className="so-largo">{admin.nome} · {admin.papel}</span>
              {admin.papel === 'dono' && (
                <>
                  <button type="button" onClick={() => usarApp.mutate('/')} disabled={usarApp.isPending} className="acao-link"><Icone nome="inicio" />Usar como cliente</button>
                  <button type="button" onClick={() => usarApp.mutate('/pro/inicio')} disabled={usarApp.isPending} className="acao-link"><Icone nome="trabalho" />Usar como profissional</button>
                </>
              )}
              <button type="button" onClick={() => sair.mutate()} className="acao-link"><Icone nome="sair" />Sair do painel</button>
            </div>
          )}
        </div>
      </header>
      {admin ? (
        <div className="admin-shell-corpo">
          <aside className="admin-sidebar" aria-label="Filas do painel">
            <m.nav layoutRoot className="nav-app" aria-label="Painel da equipe">
              {LINKS.filter((l) => visivel(l, admin)).map((l) => (
                <Link key={l.to} to={l.to as never} className="nav-app-link">
                  {({ isActive }: { isActive: boolean }) => (
                    <>
                      {isActive && <m.span layoutId="luz-admin" className="nav-app-luz" aria-hidden="true" transition={MOLA_TROCA} />}
                      <Icone nome={l.icone} />
                      <span>{l.texto}</span>
                    </>
                  )}
                </Link>
              ))}
            </m.nav>
          </aside>
          <main id="conteudo" className="admin-conteudo"><Outlet /></main>
        </div>
      ) : <main id="conteudo" className="conteudo-publico"><Outlet /></main>}
    </div>
  );
}
